import { resetComponent } from '../../style';
import { genStyleHooks } from '../../theme/internal';
import type { AliasToken, GenerateStyle } from '../../theme/internal';

export interface ComponentToken {
  filterInputTriggerHeight?: number;
  filterInputTriggerRadius?: number;
  filterInputTriggerBg?: string;
  filterInputTriggerBorder?: string;
  filterInputTriggerColor?: string;
  filterInputTriggerFontSize?: number;
  filterInputTriggerFontWeight?: number;
  filterInputTriggerTransition?: string;
  filterInputTriggerHoverBorder?: string;
  filterInputTriggerPressedBoxShadow?: string;
  filterInputTriggerPressedBorder?: string;
  filterInputTriggerPressedBg?: string;
  filterInputTriggerSelectedBoxShadow?: string;
  filterInputTriggerSelectedBorder?: string;
  filterInputTriggerSelectedBg?: string;
  filterInputTriggerDisabledBorder?: string;
  filterInputTriggerDisabledBg?: string;
  filterInputTriggerDisabledColor?: string;
  filterInputTriggerDisabledOpacity?: number;
  filterInputTriggerAffixMargin?: number;
  filterInputTriggerAffixFontSize?: number;
  filterInputTriggerPlaceholderColor?: string;
}

export interface FilterInputTriggerToken extends AliasToken, ComponentToken {
  componentCls: string;
  antCls: string;
}

export const initFilterInputTriggerToken = (
  token: FilterInputTriggerToken,
): FilterInputTriggerToken => ({
  ...token,
  filterInputTriggerHeight: 40,
  filterInputTriggerRadius: 4,
  filterInputTriggerBg: '#fdfdfd',
  filterInputTriggerBorder: '1px solid #ebecef',
  filterInputTriggerColor: '#141414',
  filterInputTriggerFontSize: 14,
  filterInputTriggerFontWeight: 500,
  filterInputTriggerTransition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
  filterInputTriggerHoverBorder: '#141414',
  filterInputTriggerPressedBoxShadow: '0 0 0 2px #e2ebff',
  filterInputTriggerPressedBorder: '1px solid #0c0554',
  filterInputTriggerPressedBg: '#c4d7ff',
  filterInputTriggerSelectedBoxShadow: 'none',
  filterInputTriggerSelectedBorder: '1px solid #0c0554',
  filterInputTriggerSelectedBg: '#e2ebff',
  filterInputTriggerDisabledBorder: '1px solid #ebecef',
  filterInputTriggerDisabledBg: '#f9f9f9',
  filterInputTriggerDisabledColor: '#898e9a',
  filterInputTriggerDisabledOpacity: 0.7,
  filterInputTriggerAffixMargin: 8,
  filterInputTriggerAffixFontSize: 16,
  filterInputTriggerPlaceholderColor: '#141414',
});

const genFilterInputTriggerStyle: GenerateStyle<FilterInputTriggerToken> = (token) => {
  const {
    componentCls,
    antCls,
    filterInputTriggerHeight,
    filterInputTriggerRadius,
    filterInputTriggerBg,
    filterInputTriggerBorder,
    filterInputTriggerColor,
    filterInputTriggerFontSize,
    filterInputTriggerFontWeight,
    filterInputTriggerTransition,
    filterInputTriggerHoverBorder,
    filterInputTriggerPressedBoxShadow,
    filterInputTriggerPressedBorder,
    filterInputTriggerPressedBg,
    filterInputTriggerSelectedBoxShadow,
    filterInputTriggerSelectedBorder,
    filterInputTriggerSelectedBg,
    filterInputTriggerDisabledBorder,
    filterInputTriggerDisabledBg,
    filterInputTriggerDisabledColor,
    filterInputTriggerDisabledOpacity,
    filterInputTriggerAffixMargin,
    filterInputTriggerAffixFontSize,
  } = token;

  return {
    [`${componentCls}-input-trigger`]: {
      ...resetComponent(token),
      position: 'relative',
      padding: `0 12px`,
      height: filterInputTriggerHeight,
      lineHeight: `${filterInputTriggerHeight}px`,
      borderRadius: filterInputTriggerRadius,
      boxSizing: 'border-box',
      background: filterInputTriggerBg,
      border: filterInputTriggerBorder,
      color: filterInputTriggerColor,
      fontSize: filterInputTriggerFontSize,
      fontWeight: filterInputTriggerFontWeight,
      transition: filterInputTriggerTransition,

      [`&:not(${componentCls}-input-trigger-disabled):not(${componentCls}-input-trigger-selected):not(${componentCls}-input-trigger-pressed):not(${componentCls}-input-trigger-hover)`]:
        {
          border: filterInputTriggerBorder,
          color: filterInputTriggerColor,
          background: filterInputTriggerBg,
        },
      [`&:not(${componentCls}-input-trigger-disabled):hover`]: {
        borderColor: filterInputTriggerHoverBorder,
        zIndex: 2,
      },
      [`&${componentCls}-input-trigger-pressed`]: {
        boxShadow: filterInputTriggerPressedBoxShadow,
        border: filterInputTriggerPressedBorder,
        background: filterInputTriggerPressedBg,
        zIndex: 3,
      },
      [`&${componentCls}-input-trigger-selected`]: {
        boxShadow: filterInputTriggerSelectedBoxShadow,
        border: filterInputTriggerSelectedBorder,
        background: `var(--base-selected-bg-color, ${filterInputTriggerSelectedBg})`,
        zIndex: 3,
      },
      [`&${componentCls}-input-trigger-disabled`]: {
        border: filterInputTriggerDisabledBorder,
        background: filterInputTriggerDisabledBg,
        color: filterInputTriggerDisabledColor,
        pointerEvents: 'none',
        opacity: filterInputTriggerDisabledOpacity,
      },
      '.rc-input-affix-wrapper': {
        display: 'inline-flex',
        width: '100%',
        height: '100%',
        '.rc-input-prefix': {
          display: 'inline-flex',
          marginRight: filterInputTriggerAffixMargin,
          fontSize: filterInputTriggerAffixFontSize,
        },
        '.rc-input-suffix': {
          display: 'inline-flex',
          marginLeft: filterInputTriggerAffixMargin,
          fontSize: filterInputTriggerAffixFontSize,
        },
      },
      'input.rc-input': {
        width: '100%',
        border: 'none',
        outline: 'none',
        fontFamily: 'inherit',
        fontWeight: 'inherit',
        color: 'inherit',
        background: 'transparent',
        '&::placeholder': {
          fontFamily: 'inherit',
          fontSize: 'inherit',
          color: 'inherit',
        },
      },
    },
    [`${componentCls}-date-picker-range-trigger`]: {
      [`&${antCls}-picker`]: {
        [`${antCls}-picker-prefix`]: {
          marginInlineEnd: 8,
        },
        [`${antCls}-picker-prefix + ${antCls}-picker-input:nth-of-type(2)`]: {
          width: '70%',
        },
        [`${antCls}-picker-clear`]: {
          color: '#141414',
          opacity: 1,
          '&:hover': {
            color: '#141414',
          },
        },
      },
      [`&${antCls}-picker-outlined`]: {
        borderColor: '#ebecef',
        '&:hover': {
          borderColor: '#141414',
        },
        [`&:has(${antCls}-picker-input > input:not(:placeholder-shown))`]: {
          borderColor: '#141414',
        },
      },
    },
  };
};

export const useStyle = genStyleHooks(['Filter', 'Component'], (token) => [
  genFilterInputTriggerStyle(initFilterInputTriggerToken(token)),
]);
